import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {t} from 'i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';
import _ from 'lodash';
import StandardReports from '../../StandardReports';
import {Reports, RightPanelConfig} from '../../config/reportTypes';
import './TImelineContainer.scss';
import {
  useExportExcelGapReport,
  useGetReportTimeline,
  useGetReportTimelineResult,
  useGetReportTimelineView,
  useTimelineToReportMasterDocId,
  useTimelineBackground,
  useTimelineTotallyReportMasterDocId,
  useTimelineDoc,
  useGetReportState,
  useTimelineGapExporrToExcel,
} from '../../../../../hooks/reporting/useReporting';
import {
  getAppliedFilterValueObject,
  getBulkActionExecutePayload,
  getBulkActionSourceDetails,
  getSelectedIngestions,
  getTimelineViewPayload,
  mergeTimelineApiDataIntoConfig,
  sourcesSectionKeys,
  TimelineReportType,
  updateApplyButtonState,
} from './Helper';
import {
  ReportActionMenuOptionTypes,
  ReportTypeKey,
  ReportTypes,
  SourceTypeCode,
  SourceTypeContextCode,
  TimelineTypes,
  ToastType,
} from '../../../../../shared/constants';
import {ToastMessageConfigType} from '../../../../../api/types/ingestionApi.type';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {SourceItem} from '../../../tally/selectSourceContainer/ArrSourceSelectedContainer';
import TimelineCharts from './components/TimelineCharts';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  GetTimeLineResponse,
  TimelineResponse,
} from '../../../../../api/types/reportingApi.type';
import {CustomMenuItem} from '../../../../common/arr-menu/ArrMenu';
import {
  SelectedBarInfoType,
  TimelineType,
} from '../../../../common/arr-selectable-column-chart/ArrSelectableColumnChart';
import {
  getBarActualValueFromIndex,
  totalDocumentToView,
} from '../../../../common/arr-selectable-column-chart/chartUtils';
import ArrWaitToCompleteModalWrapper from '../../component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {setExportDataIngestion} from '../../../../../redux/slice/ExportDataSlice';
import {useConfigKeyValue} from '../../../../../hooks/categorization/useCategorization';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../../redux/slice/docListSlice';
import ArrDocMaxLimitWarning from '../../../../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import {
  clearAllTimelineData,
  clearTimelineIngestion,
  setAppliedFilters,
  setGeneratedReportData,
  setLeftPanelData,
  setTimelineConfig,
  setTimelineResult,
} from '../../../../../redux/slice/timelineGapReportSlice';
import {
  storeDocumentListMasterId,
  storeSourceTypeCode,
} from '../../../../../redux/slice/docViewSlice';
import {ISelectItem} from '../../../../../shared/types';

dayjs.extend(utc);

const stringPath = 'reports.timelineAndGaps.';

const TIMELINE_OPTION: ISelectItem = {
  value: ReportTypeKey.TIMELINE_AND_GAPS,
};

const TimelineContainer: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isResetExecuted = useRef<boolean>(false);

  const prevPath = useRef(location.pathname);
  const initialJsonConfig = useRef<any | null>(null);
  const loadedReportIdFromUrl = useRef<number | null>(null);

  /** Cache data of timeline report from redux state */
  const {
    selectedIngestion,
    leftPanelData: storeLeftPanelData,
    timelineResult: storedTimelineResult,
    reportConfig: storeReportConfig,
    appliedFilters: storedAppliedFilters,
    generatedReportData: storedGeneratedReportData,
  } = useSelector((state: RootState) => state.timelineGapReport);

  // Storing generated reportId and its config data for use to result and save report
  const timelineReportConfigData = useRef<GetTimeLineResponse | null>(null);

  // Storing to keep track of selected duration from timeline charts when actin is picked
  const selectedTimelineDuration = useRef<{[key: string]: number}[]>([]);

  // tracking current selected bars for export action
  const selectedBarsForExport = useRef<
    Map<string, Map<string, SelectedBarInfoType>>
  >(new Map());

  // Storing left panel view api data
  const [timelineViewData, setTimelineViewData] =
    useState<TimelineResponse | null>(null);
  // Storing result data of timeline report for right side panel
  const [timelineResultData, setTimelineResultData] = useState<Record<
    string,
    any
  > | null>(null);

  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );

  const [reportConfig, setReportConfig] = useState<any | null>(null);

  const [isQueryRun, setIsQueryRun] = useState(false);

  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({show: false});

  const [appliedFilterValues, setAppliedFilterValues] = useState<Record<
    string,
    any
  > | null>(null);

  const [totalDocumentCount, setTotalDocumentCount] = useState(0);

  const [docPayload, setDocPayload] = useState({});

  const [destination, setDestination] = useState<SourceTypeCode>(
    SourceTypeCode.DOCVIEW
  );

  const [showDocLimitDialog, setShowDocLimitDialog] = useState(false);

  const [selectedAction, setSelectedAction] = useState('');

  const isTimelineLeftPanelAPIDataMapped = useRef<boolean>(false);

  const executeFetchReportForType = useRef<TimelineReportType>('YEARLY');

  /** Fetching timeline view data for left side panel */
  const {
    mutate: getTimelineView,
    data: timelineViewResponse,
    isPending: isLoadingTimelineView,
    isError: isErrorTimelineView,
  } = useGetReportTimelineView();

  /** Generating reportId using left panel's selected value for rightside result view*/
  const {mutate: getReportTimeline, isPending: isLoadingReportTimeline} =
    useGetReportTimeline();

  const {
    mutate: callTimelineToReportMasterDocId,
    isPending: isLoadingTimelineToReportMasterDocId,
  } = useTimelineToReportMasterDocId();

  const {
    mutate: getTimelineTotallyDocMasterDocId,
    isPending: isLoadingTimelineTotallyMasterDocId,
  } = useTimelineTotallyReportMasterDocId();

  /** Fetching timeline result data for right side panel usin reportID */
  const {mutate: getReportTimelineResult, isPending: isLoadingTimelineResult} =
    useGetReportTimelineResult();

  /** To generate gap report for selected Duration of timeline charts */
  const {
    mutate: exportExcelGapReport,
    isPending: isLoadingExportExcelGapReport,
  } = useExportExcelGapReport();

  const {
    mutate: executeTimeLineBackground,
    isPending: isTimelineBackgroundPending,
  } = useTimelineBackground();

  const {mutate: getTimelineDoc, isPending: isTimelineDocPending} =
    useTimelineDoc();

  const {refetch: fetchConfig} = useConfigKeyValue(destination);

  const reportIdFromUrl = useMemo(() => {
    const key = location.pathname.split('/')[2];
    const pathParam = location.search;
    const urlParams = new URLSearchParams(pathParam);
    let id = urlParams.get('ReportId') ?? 0;
    if (key === 'timeline') {
      id =
        urlParams.get('ReportID') ??
        urlParams.get('reportId') ??
        urlParams.get('ReportId') ??
        urlParams.get('reportID') ??
        '';
    }
    const parsed = Number(id);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [location.pathname, location.search]);

  const {
    refetch: fetchReportState,
    isLoading: isReportStateLoading,
    isError: isReportStateError,
    data: savedReportStateData,
    error: reportStateError,
  } = useGetReportState(reportIdFromUrl.toString());

  const {
    mutate: exportExcelTimlineGapReport,
    isPending: isLoadingExportExcelTimelineGapReport,
  } = useTimelineGapExporrToExcel();

  const reportKey = useMemo(() => {
    const key = location.pathname.split('/')[2];
    if (key === 'timeline') {
      return 'Timeline';
    }
    return key;
  }, [location.pathname]);

  useEffect(() => {
    // To reload report page when report id is updated in browser by open other notification
    if (reportIdFromUrl !== 0 && loadedReportIdFromUrl.current !== null) {
      // id updated in url so reset all state amd fetch report data again
      loadedReportIdFromUrl.current = reportIdFromUrl;
      isResetExecuted.current = false;
      // Clear redux store cache on reset
      dispatch(clearAllTimelineData());
      resetStateToInitial();
      setAppliedFilterValues(null);
      setReportConfig(null);
      fetchReportState();
      return;
    }
    /** Checking reportId's data exist in local redux state from url.
     * this is to handle show data without api call when user navigates back
     * from doclist or other page using Action menu options.
     */
    if (
      reportIdFromUrl === storedGeneratedReportData?.reportID &&
      !(location.state?.fromReportLanding || location.state?.isBackgroundReport)
    ) {
      timelineReportConfigData.current = storedGeneratedReportData;
      setTimelineViewData(storeLeftPanelData as TimelineResponse);
      setTimelineResultData(storedTimelineResult);
      isTimelineLeftPanelAPIDataMapped.current = true;
      setAppliedFilterValues(storedAppliedFilters);
      setIsQueryRun(true);
      const config = mergeTimelineApiDataIntoConfig(
        storeReportConfig,
        storeLeftPanelData as TimelineResponse,
        storedAppliedFilters,
        true
      );
      setReportConfig(
        updateApplyButtonState(
          config as Reports,
          storedAppliedFilters ?? {},
          true
        )
      );
    } else {
      // reset data on load to avoid last report garbase data visible on browser back
      resetStateToInitial();
      setAppliedFilterValues(null);
      setReportConfig(null);
      resetStateToInitial();
      loadedReportIdFromUrl.current = reportIdFromUrl;
      loadReportConfig().then(() => {
        if (
          (location.state?.fromReportLanding ||
            location.state?.isBackgroundReport) &&
          reportIdFromUrl !== 0
        ) {
          fetchReportState();
        } else {
          getTimelineView({reportID: 0});
        }
      });
    }
  }, [reportIdFromUrl]);

  useEffect(() => {
    if (!isReportStateLoading && !isReportStateError && savedReportStateData) {
      getTimelineView({reportID: reportIdFromUrl});
    } else if (!isReportStateLoading && isReportStateError) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          (reportStateError as any)?.response?.data?.title ??
          t(stringPath + 'reportStateAPIError'),
      });
    }
  }, [isReportStateError, isReportStateLoading, savedReportStateData]);

  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      dispatch(clearTimelineIngestion());
    }
    prevPath.current = location.pathname;
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (reportConfig) {
      dispatch(setTimelineConfig(JSON.parse(JSON.stringify(reportConfig))));
    }
  }, [reportConfig]);

  useEffect(() => {
    if (
      !isLoadingTimelineView &&
      !isErrorTimelineView &&
      timelineViewResponse &&
      initialJsonConfig.current
    ) {
      isTimelineLeftPanelAPIDataMapped.current = true;
      setTimelineViewData(timelineViewResponse as TimelineResponse);
      dispatch(setLeftPanelData(timelineViewResponse as Record<string, any>));
      const mergedConfig = mergeTimelineApiDataIntoConfig(
        initialJsonConfig.current,
        timelineViewResponse as TimelineResponse,
        null,
        false,
        savedReportStateData
      );
      setReportConfig(mergedConfig);

      if (location.state?.isBackgroundReport && savedReportStateData) {
        const resultData = {
          reportID: reportIdFromUrl,
          timelineFilterResponse: savedReportStateData.timelineFilter,
        };
        timelineReportConfigData.current = {
          reportType: 'TGR012',
          reportID: reportIdFromUrl,
          actionType: '',
          reportFilePath: '',
          notificationID: 0,
          reportName: '',
        };
        const appliedFilters = getAppliedFilterValueObject(
          mergedConfig as Reports,
          resultData
        );
        setIsQueryRun(true);
        setTimelineResultData(resultData);
        setAppliedFilterValues(appliedFilters);
        dispatch(setGeneratedReportData(timelineReportConfigData.current));
        dispatch(
          setTimelineResult({
            reportID: reportIdFromUrl,
            timelineFilterResponse: savedReportStateData.timelineFilter,
          })
        );
        dispatch(setAppliedFilters(appliedFilters));
      }
    }
  }, [
    timelineViewResponse,
    isLoadingTimelineView,
    isErrorTimelineView,
    reportKey,
  ]);

  const handleExportClick = () => {
    const updatedselectionCriteria = {...saveReportConfig};

    selectedBarsForExport.current.forEach((selectedBar, timelineType) => {
      switch (timelineType) {
        case 'YEARLY':
          updatedselectionCriteria.YearlySelectedPeriod =
            getBarActualValueFromIndex(
              timelineResultData?.timelineFilterResponse.yearlyData ?? [],
              selectedBar,
              timelineType.toLocaleLowerCase() as TimelineType
            );
          break;
        case 'MONTHLY':
          updatedselectionCriteria.MonthlySelectedPeriod =
            getBarActualValueFromIndex(
              timelineResultData?.timelineFilterResponse.monthlyData ?? [],
              selectedBar,
              timelineType.toLocaleLowerCase() as TimelineType
            );
          break;
        case 'DAILY':
          updatedselectionCriteria.DailySelectedPeriod =
            getBarActualValueFromIndex(
              timelineResultData?.timelineFilterResponse.dailyData ?? [],
              selectedBar,
              timelineType.toLocaleLowerCase() as TimelineType
            );
          break;
        default:
          break;
      }
    });
    const payload = {
      actionType: 'EXPORTREPORT',
      reportTypeCode: 'TGR012',
      selectionCriteria: updatedselectionCriteria,
    };

    exportExcelTimlineGapReport(payload, {
      onSuccess: () => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Success,
          message: t('reports.common.reportExportSuccess'),
        });
      },
      onError: (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message:
            error?.response?.data?.title ??
            t('reports.common.reportExportError'),
        });
      },
    });
  };

  const getActionMenuSources = (
    type: TimelineReportType,
    selectedBar: Map<string, SelectedBarInfoType>
  ) => {
    const payload = getTimelineViewPayload(
      type,
      timelineViewData,
      appliedFilterValues ?? {},
      timelineResultData,
      [],
      true,
      selectedIngestion
    );
    switch (type) {
      case 'YEARLY':
        payload.YearlySelectedPeriod = getBarActualValueFromIndex(
          timelineResultData?.timelineFilterResponse.yearlyData ?? [],
          selectedBar,
          type.toLocaleLowerCase() as TimelineType
        );
        break;
      case 'MONTHLY':
        payload.MonthlySelectedPeriod = getBarActualValueFromIndex(
          timelineResultData?.timelineFilterResponse.monthlyData ?? [],
          selectedBar,
          type.toLocaleLowerCase() as TimelineType
        );
        break;
      case 'DAILY':
        payload.DailySelectedPeriod = getBarActualValueFromIndex(
          timelineResultData?.timelineFilterResponse.dailyData ?? [],
          selectedBar,
          type.toLocaleLowerCase() as TimelineType
        );
        break;
      default:
        break;
    }
    return payload;
  };

  const saveReportConfig = useMemo(() => {
    if (timelineReportConfigData.current?.reportID && appliedFilterValues) {
      const payload = getTimelineViewPayload(
        'YEARLY',
        timelineViewData,
        appliedFilterValues ?? {},
        timelineResultData,
        selectedTimelineDuration.current,
        true,
        selectedIngestion
      );
      payload.reportTypeCode = timelineReportConfigData.current?.reportType;
      payload.reportId = timelineReportConfigData.current?.reportID;
      return payload;
    }
    return undefined;
  }, [
    timelineViewData,
    appliedFilterValues,
    timelineResultData,
    selectedTimelineDuration.current,
    timelineReportConfigData.current,
  ]);

  const rightPanelHeaderConfig = useMemo<RightPanelConfig>(
    () => ({
      title: t(stringPath + 'totals'),
      subTitle: t(stringPath + 'dateAsOf', {
        date: dayjs(
          timelineResultData?.timelineFilterResponse?.dateAsOf ?? undefined
        )
          .utc()
          .format('YYYY/MM/DD HH:mm:ss'),
      }),
      showUploadIcon: true,
      showSaveIcon: true,
      showColumnsIcon: false,
      showActionButton: false,
      onUploadClick: handleExportClick,
    }),
    [timelineResultData, t, saveReportConfig, selectedBarsForExport.current]
  );

  const executeExportExcelGapReport = (reportType: TimelineReportType) => {
    const payload = getTimelineViewPayload(
      reportType,
      timelineViewData,
      appliedFilterValues ?? {},
      timelineResultData,
      selectedTimelineDuration.current,
      true,
      selectedIngestion
    );
    exportExcelGapReport(
      {
        queryParam: {gapReportType: reportType},
        payload: payload,
      },
      {
        onSuccess: () => {
          setToastMsgConfig({
            show: true,
            type: ToastType.Success,
            message: t(stringPath + 'exportToExcelGapSuccess'),
          });
        },
        onError: (error: any) => {
          setToastMsgConfig({
            show: true,
            type: ToastType.Error,
            message:
              (error as any)?.response?.data?.title ??
              t('common.exportToExcelGapFailure'),
          });
        },
      }
    );
  };

  const executeExportTimelineToReport = (
    reportType: TimelineReportType,
    selectedBar: Map<string, SelectedBarInfoType>
  ) => {
    const commonPayload = {
      actionTypes: SourceTypeCode.EXPORTDATA,
      sourceModel: getActionMenuSources(
        (reportType as any).toUpperCase(),
        selectedBar
      ),
      reportTypes: reportType,
      isDocument: true,
    };
    callTimelineToReportMasterDocId(commonPayload, {
      onSuccess: (response) => {
        const masterDocIdString = response.reportMasterDocListId.toString();
        const backPath = `${window.location.pathname}`;
        dispatch(
          setExportDataIngestion({
            openFrom: 'TIMELINE',
            reportMasterDocListId: masterDocIdString,
            sourceName: 'DOCUMENTS',
            backSourceLabel: 'Timeline',
            backSourcePath: `${backPath}?ReportId=${timelineReportConfigData.current?.reportID}`,
          })
        );
        navigate('/Report/ExportData', {
          state: {
            item: {
              navigationSourceType: 'TIMELINE',
              source: masterDocIdString,
              sourceName: 'documents',
              sourceDisable: true,
            },
          },
        });
      },
      onError: (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message:
            (error as any)?.response?.data?.title ??
            t('common.timelineToReportExportDataFailure'),
        });
      },
    });
  };

  const getTimelineTypeData = (timeline: TimelineType) => {
    switch (timeline.toUpperCase()) {
      case TimelineTypes.YEARLY:
        return timelineResultData?.timelineFilterResponse.yearlyData;
      case TimelineTypes.MONTHLY:
        return timelineResultData?.timelineFilterResponse.monthlyData;
      case TimelineTypes.DAILY:
        return timelineResultData?.timelineFilterResponse.dailyData;
      default:
        return [];
    }
  };

  const loadReportConfig = useCallback(async () => {
    if (!reportKey) return;
    try {
      const data = await import(`../../config/${reportKey}.json`);
      initialJsonConfig.current = data;
      return data;
    } catch (error) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message: t(stringPath + 'reportConfigAPIError'),
      });
      console.log('Error loading JSON:', error);
    }
  }, [reportKey, timelineViewData]);

  const getTallyPayload = (
    timelineType: TimelineReportType,
    selectedBar: Map<string, SelectedBarInfoType>
  ) => ({
    actionTypes: SourceTypeCode.TALLY,
    reportTypes: 'timelineType',
    isDocument: true,
    sourceModel: getActionMenuSources(timelineType, selectedBar),
  });

  const handleActionPick = (
    item: CustomMenuItem,
    selectedBar: Map<string, SelectedBarInfoType>,
    timelineType: TimelineType
  ) => {
    if (String(item.id || '').toLowerCase() === 'tally') {
      const payload = getTallyPayload(
        timelineType.toUpperCase() as TimelineReportType,
        selectedBar
      );
      getTimelineTotallyDocMasterDocId(payload, {
        onSuccess: (response) => {
          navigate('/Report/Tally', {
            state: {
              navigationSourceType: 'TIMELINE',
              source: [response.reportMasterDocListId.toString()],
              sourceName: 'DOCLIST',
              backSourcePath: `${location.pathname}?ReportId=${timelineReportConfigData.current?.reportID}`,
            },
          });
        },
        onError: (error: any) => {
          setToastMsgConfig({
            show: true,
            type: ToastType.Error,
            message: error?.message ?? '',
          });
        },
      });
      return;
    }

    switch (item.id) {
      case ReportActionMenuOptionTypes.GenerateGap:
        executeExportExcelGapReport(
          timelineType.toUpperCase() as TimelineReportType
        );
        return;
      case ReportActionMenuOptionTypes.Monthly:
        if (timelineType === 'yearly') {
          selectedTimelineDuration.current = getBarActualValueFromIndex(
            timelineResultData?.timelineFilterResponse.yearlyData ?? [],
            selectedBar,
            timelineType
          );
          executeGetTimelineReport('MONTHLY', appliedFilterValues || {});
        }
        break;
      case ReportActionMenuOptionTypes.Daily:
        if (timelineType === 'monthly') {
          selectedTimelineDuration.current = getBarActualValueFromIndex(
            timelineResultData?.timelineFilterResponse.monthlyData ?? [],
            selectedBar,
            timelineType
          );
          executeGetTimelineReport('DAILY', appliedFilterValues || {});
        }
        break;
      case ReportActionMenuOptionTypes.ExportData: {
        executeExportTimelineToReport(
          timelineType.toUpperCase() as TimelineReportType,
          selectedBar
        );
        break;
      }
      case ReportActionMenuOptionTypes.ViewInDocList: {
        setDestination(SourceTypeCode.DOCLIST);
        selectedTimelineDuration.current = getBarActualValueFromIndex(
          getTimelineTypeData(timelineType),
          selectedBar,
          timelineType
        );
        setTotalDocumentCount(totalDocumentToView(selectedBar));
        const payload = {
          reportTypes: timelineType,
          threasholdFlag: true,
          actionTypes: SourceTypeCode.DOCLIST,
          isDocumentCount: false,
          sourceModel: getActionMenuSources(
            (timelineType as any).toUpperCase(),
            selectedBar
          ),
        };
        setShowDocLimitDialog(true);
        setDocPayload(payload);
        break;
      }
      case ReportActionMenuOptionTypes.ViewInDocView:
      case ReportActionMenuOptionTypes.ViewInDocViewNewTab: {
        setSelectedAction(item.id);
        setDestination(SourceTypeCode.DOCVIEW);
        selectedTimelineDuration.current = getBarActualValueFromIndex(
          getTimelineTypeData(timelineType),
          selectedBar,
          timelineType
        );
        setTotalDocumentCount(totalDocumentToView(selectedBar));
        const payload = {
          reportTypes: timelineType.toUpperCase(),
          actionTypes: SourceTypeCode.DOCVIEW,
          isDocumentCount: false,
          sourceModel: getActionMenuSources(
            (timelineType as any).toUpperCase(),
            selectedBar
          ),
        };
        setShowDocLimitDialog(true);
        setDocPayload(payload);
        break;
      }
      default:
        break;
    }
  };

  const viewInDocList = () => {
    setShowDocLimitDialog(false);
    fetchConfig();
    executeTimeLineBackground(docPayload as any, {
      onSuccess: (response) => {
        const masterId =
          response.documentListID ?? response.reportMasterDoclistId ?? 0;
        const backPath = `${location.pathname}?ReportId=${timelineReportConfigData.current?.reportID}`;
        dispatch(setDocListId(Number(masterId)));
        dispatch(
          setDocListBackNavigation({
            label: t(
              'reports.communicationExplorer.actionMenuItems.timelineAndGaps'
            ),
            path: backPath,
          })
        );
        navigate('/Document/DocList');
      },
      onError: (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message: error?.message ?? '',
        });
      },
    });
  };

  const viewInDocView = () => {
    setShowDocLimitDialog(false);
    fetchConfig();
    getTimelineDoc(docPayload as any, {
      onSuccess: (response) => {
        const masterId =
          response.docListID ?? response.reportMasterDoclistId ?? 0;
        dispatch(storeDocumentListMasterId(String(masterId)));
        dispatch(storeSourceTypeCode(SourceTypeCode.TIMELINE));

        if (selectedAction === ReportActionMenuOptionTypes.ViewInDocView) {
          navigate('/DocumentViewer/DocView');
        } else {
          const newWindow = window.open('about:blank', '_blank');
          newWindow &&
            (newWindow.location.href = `${window.location.origin}/DocumentViewer/DocView`);
        }
      },
      onError: (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message: error?.message ?? '',
        });
      },
    });
  };

  const handleBarSelectionUpdate =
    (type: string) => (selectedBar: Map<string, SelectedBarInfoType>) => {
      selectedBarsForExport.current.set(type, selectedBar);
    };
  const userRole = useSelector(
    (state: RootState) => state?.authenticatedUser?.user?.roleName
  );

  const sources: SourceItem[] = useMemo(() => {
    if (!timelineViewData) return [];
    const src: any = Array.isArray(timelineViewData)
      ? timelineViewData[0]
      : timelineViewData;
    const data: SourceItem[] = [];
    sourcesSectionKeys.forEach((key) => {
      switch (key) {
        case 'SECURITYGROUPS':
          data.push({
            id: key,
            label: t(stringPath + 'securityGroups'),
            data: src?.timelineModel?.securityGroupList ?? [],
          });
          break;
        case 'SEARCHES':
          data.push({
            id: key,
            label: t(stringPath + 'searches'),
            data: src?.timelineModel?.searchTreeList ?? [],
          });
          break;
        case 'ASSIGNMENTS':
          data.push({
            id: key,
            label: t(stringPath + 'assignments'),
            data: src?.timelineModel?.assignmentTreeList ?? [],
          });
          break;
        case 'FOLDERS':
          data.push({
            id: key,
            label: t(stringPath + 'folders'),
            data: src?.timelineModel?.folderTreeList ?? [],
          });
          break;
        case 'TAGS':
          data.push({
            id: key,
            label: t(stringPath + 'tags'),
            data: src?.timelineModel?.tagTreeList ?? [],
          });
          break;
        case 'INGESTION':
          if (selectedIngestion) {
            data.push({
              id: key,
              label: selectedIngestion.sourceName,
              data: [selectedIngestion.source],
            });
          }
          break;
        case 'PROJECTS':
          if (userRole === 'Project Administrator') {
            data.push({
              id: key,
              label: t(stringPath + 'projects'),
              data: src?.timelineModel?.projectList ?? [],
            });
          }
          break;
        default:
          break;
      }
    });
    return data;
  }, [timelineViewData, t, selectedIngestion, userRole]);

  const timelineChartView = useMemo(() => {
    if (!isQueryRun || !timelineResultData)
      return <div data-testid={`timeline-container-query-not-run`} />;
    const timelineData = timelineResultData?.timelineFilterResponse;
    if (!timelineData)
      return <div data-testid={`timeline-container-no-timeline-data`} />;
    return (
      <div
        className="timeline-render-data"
        data-testid={`timeline-container-div`}
      >
        {(timelineData.yearlyData ?? []).length > 0 && (
          <TimelineCharts
            data={{
              yearlyData: timelineData.yearlyData ?? [],
              monthlyData: [],
              dailyData: [],
            }}
            roleId={roleId}
            loading={isLoadingTimelineResult}
            timelineType={'YEARLY'}
            onActionPick={handleActionPick}
            actionMenuAditioanlProp={{
              sourceType: SourceTypeCode.TIMELINE,
              sourceTypeContext: SourceTypeContextCode.TIMELINENOC,
            }}
            getSourceDetails={(selectedBars) =>
              getActionMenuSources('YEARLY', selectedBars)
            }
            getDoActionData={getBulkActionExecutePayload}
            getUpdatedSourceDetails={getBulkActionSourceDetails}
            onUpdateSelectedBar={handleBarSelectionUpdate('YEARLY')}
          />
        )}
        {(timelineData.monthlyData ?? []).length > 0 && (
          <TimelineCharts
            data={{
              yearlyData: timelineData.yearlyData ?? [],
              monthlyData: timelineData.monthlyData ?? [],
              dailyData: [],
            }}
            roleId={roleId}
            loading={isLoadingTimelineResult}
            timelineType={'MONTHLY'}
            onActionPick={handleActionPick}
            actionMenuAditioanlProp={{
              sourceType: SourceTypeCode.TIMELINE,
              sourceTypeContext: SourceTypeContextCode.TIMELINENOC,
            }}
            getSourceDetails={(selectedBars) =>
              getActionMenuSources('MONTHLY', selectedBars)
            }
            getDoActionData={getBulkActionExecutePayload}
            getUpdatedSourceDetails={getBulkActionSourceDetails}
            onUpdateSelectedBar={handleBarSelectionUpdate('MONTHLY')}
          />
        )}
        {(timelineData.dailyData ?? []).length > 0 && (
          <TimelineCharts
            data={{
              yearlyData: timelineData.yearlyData ?? [],
              monthlyData: timelineData.monthlyData ?? [],
              dailyData: timelineData.dailyData ?? [],
            }}
            roleId={roleId}
            loading={isLoadingTimelineResult}
            timelineType={'DAILY'}
            onActionPick={handleActionPick}
            actionMenuAditioanlProp={{
              sourceType: SourceTypeCode.TIMELINE,
              sourceTypeContext: SourceTypeContextCode.TIMELINENOC,
            }}
            getSourceDetails={(selectedBars) =>
              getActionMenuSources('DAILY', selectedBars)
            }
            getDoActionData={getBulkActionExecutePayload}
            getUpdatedSourceDetails={getBulkActionSourceDetails}
            onUpdateSelectedBar={handleBarSelectionUpdate('DAILY')}
          />
        )}
      </div>
    );
  }, [timelineResultData, isLoadingTimelineResult, isQueryRun]) as
    | ReactElement
    | undefined;

  const getInitialSelectedSources = () => {
    if (selectedIngestion) {
      return getSelectedIngestions(selectedIngestion);
    } else if (
      storedAppliedFilters &&
      reportIdFromUrl === storedGeneratedReportData?.reportID
    ) {
      const sectionkey = sourcesSectionKeys.find(
        (key) => _.get(storedAppliedFilters, key) !== undefined
      );
      if (sectionkey) {
        return {
          [sectionkey]: _.get(
            storedAppliedFilters,
            `${sectionkey}[0].source`,
            []
          ),
        };
      }
      return undefined;
    } else if (
      (location.state?.fromReportLanding ||
        location.state?.isBackgroundReport) &&
      reportIdFromUrl !== 0 &&
      savedReportStateData?.timelineFilter &&
      !isResetExecuted.current
    ) {
      return {
        [savedReportStateData?.timelineFilter.sourceType]:
          savedReportStateData?.timelineFilter.sourceID ?? [],
      };
    }
    return undefined;
  };
  const handleActionClick = (
    actionKey: string,
    values: Record<string, any>
  ) => {
    if (actionKey === 'applyChanges') {
      dispatch(setAppliedFilters(values));
      setAppliedFilterValues(values);
      executeGetTimelineReport('YEARLY', values);
    } else if (actionKey === 'reset') {
      isResetExecuted.current = true;
      setAppliedFilterValues(null);
      resetStateToInitial();
      // Clear redux store cache on reset
      dispatch(clearAllTimelineData());

      // Reset report config to initial state on reset
      const config = mergeTimelineApiDataIntoConfig(
        reportConfig,
        timelineViewData as TimelineResponse
      );
      setReportConfig(updateApplyButtonState(config as Reports, {}, false));

      if (reportIdFromUrl !== 0) {
        navigate(location.pathname, {
          replace: true,
          state: {
            isBackgroundReport: false,
          },
        });
      }
    }
  };

  const handleFilterChange = useCallback((values: Record<string, any>) => {
    setReportConfig((prevReportConfig: Reports | null) => {
      if (!prevReportConfig) return prevReportConfig;
      return updateApplyButtonState(prevReportConfig, values, isQueryRun);
    });
  }, []);

  const resetStateToInitial = () => {
    setIsQueryRun(false);
    setTimelineResultData(null);
    timelineReportConfigData.current = null;
    selectedTimelineDuration.current = [];
    selectedBarsForExport.current = new Map();
  };

  const fetchReportData = (
    timeLineType: TimelineReportType,
    values: Record<string, any>
  ) => {
    const payload: Record<string, any> = {
      reportFilePath: timelineReportConfigData.current?.reportFilePath ?? '',
      reportId: timelineReportConfigData.current?.reportID ?? 0,
      reportName: timelineReportConfigData.current?.reportName ?? '',
      timelineFilter: getTimelineViewPayload(
        timeLineType,
        timelineViewData,
        values,
        timelineResultData,
        selectedTimelineDuration.current,
        false,
        selectedIngestion
      ),
    };
    getReportTimelineResult(payload, {
      onSuccess: (data) => {
        setIsQueryRun(true);
        setTimelineResultData(data);
        dispatch(setTimelineResult(data));
      },
      onError: (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message:
            (error as any)?.response?.data?.title ??
            t(stringPath + 'getTimelineReportFailedMsg'),
        });
      },
    });
  };

  const executeGetTimelineReport = (
    timeLineType: TimelineReportType,
    values: Record<string, any>
  ) => {
    const payload = getTimelineViewPayload(
      timeLineType,
      timelineViewData,
      values,
      timelineResultData,
      selectedTimelineDuration.current,
      false,
      selectedIngestion
    );
    if (timeLineType === 'YEARLY') resetStateToInitial();
    getReportTimeline(payload, {
      onSuccess: (data) => {
        timelineReportConfigData.current = data;
        executeFetchReportForType.current = timeLineType;
        dispatch(setGeneratedReportData(data));
      },
      onError: (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message:
            error?.response?.data?.title ??
            t(stringPath + 'getTimelineReportFailedMsg'),
        });
      },
    });
  };

  if (
    _.isNil(reportConfig) ||
    isLoadingTimelineView ||
    !isTimelineLeftPanelAPIDataMapped.current
  )
    return <ArrLoadingSpinner isOpen overlay />;

  return (
    <>
      <StandardReports
        validateActionFromParent
        className="timeline-container"
        title={reportConfig?.title || location?.state?.item?.label || ''}
        showInfoIcon={reportConfig.showInfoIcon}
        infoContent={reportConfig.infoContent}
        reportsConfig={reportConfig.config}
        onAction={handleActionClick}
        onFilterChange={handleFilterChange}
        mapRows={(data) => data}
        data={isQueryRun ? [{id: 1}] : []}
        loading={isLoadingTimelineView}
        enableSave={isQueryRun}
        isQueryRun={isQueryRun}
        rightContentComponent={timelineChartView}
        columns={[]}
        sources={sources}
        initialSelectedSources={getInitialSelectedSources()}
        disableSourceSelection={selectedIngestion !== null}
        selectedOption={TIMELINE_OPTION}
        rightPanelHeaderConfig={rightPanelHeaderConfig}
        saveReportConfig={saveReportConfig}
        saveReportType="timelineGap"
        observReportConfigUpdate={false}
      />
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      )}
      {(isLoadingReportTimeline ||
        isLoadingTimelineResult ||
        isLoadingExportExcelGapReport ||
        isLoadingTimelineTotallyMasterDocId ||
        isLoadingTimelineToReportMasterDocId ||
        isTimelineBackgroundPending ||
        isReportStateLoading ||
        isTimelineDocPending ||
        isLoadingExportExcelTimelineGapReport) && (
        <ArrLoadingSpinner isOpen overlay />
      )}
      {timelineReportConfigData.current?.notificationID &&
        timelineReportConfigData.current?.reportID && (
          <ArrWaitToCompleteModalWrapper
            showClose={false}
            reportId={timelineReportConfigData.current.reportID}
            notificationId={timelineReportConfigData.current.notificationID}
            onComplete={(reportId) => {
              if (timelineReportConfigData.current?.reportID === reportId)
                fetchReportData(
                  executeFetchReportForType.current,
                  appliedFilterValues ?? {}
                );
            }}
          />
        )}
      <ArrDocMaxLimitWarning
        totalDocumentCount={totalDocumentCount}
        show={showDocLimitDialog}
        destination={destination}
        onContinue={
          destination === SourceTypeCode.DOCLIST ? viewInDocList : viewInDocView
        }
        onCancel={() => setShowDocLimitDialog(false)}
      />
    </>
  );
};

export default TimelineContainer;
